/* ═══════════════════════════════════════════════════════════════
   DesignBriefPanel — the full design-brief form (Sheet): scope,
   applicable codes (add/remove rows), occupancy, construction type,
   owner preferences, design criteria, square footage, and custom
   title/value fields (migration 055, 2026-09-30 — Josh: "beef up
   design brief to have the ability to manage additional custom
   fields (add, edit, remove etc) both the title and of course its
   content"). Dirty → confirm-dialog on close (EDIT-COST LAW: "manage
   a record" → side panel with a real Save).

   Custom-field edits are deferred to the panel's Save button, same as
   the built-in applicable-codes list — every row lives in local state
   until Save, so Cancel/discard (the panel's existing confirm-dialog
   contract) also undoes custom-field adds/edits/removes, not just the
   fixed columns.
   ═══════════════════════════════════════════════════════════════ */
import React, { useEffect, useRef, useState } from 'react';
import { ArrowDown, ArrowUp, Plus, X } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from '../../../components/ui/sheet';
import { ConfirmDialog } from '../../../components/ui/confirm-dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Icon } from '../../../components/ui/Icon';
import type {
  ProjectDesignBrief,
  DesignBriefCode,
  DesignBriefField,
} from '../../../../shared/entities';
import type { ProjectWidgetCtx } from '../widgets/context';

function blankBrief(projectId: string): ProjectDesignBrief {
  return { id: '', projectId, applicableCodes: [], createdAt: '', updatedAt: '' };
}

function blankCustomField(projectId: string, sortOrder: number): DesignBriefField {
  return {
    id: '',
    projectId,
    title: 'New field',
    value: '',
    sortOrder,
    createdAt: '',
    updatedAt: '',
  };
}

const textareaStyle: React.CSSProperties = {
  width: '100%',
  padding: 'var(--space-2)',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--bd)',
  background: 'var(--ib)',
  color: 'var(--tx)',
  fontSize: 'var(--font-size-base)',
  fontFamily: 'var(--font-sans)',
  resize: 'vertical',
};

export function DesignBriefPanel({
  ctx,
  open,
  onClose,
}: {
  ctx: ProjectWidgetCtx;
  open: boolean;
  onClose: () => void;
}) {
  const base = ctx.designBrief.data || blankBrief(ctx.project.id);
  const [draft, setDraft] = useState<ProjectDesignBrief>(base);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  // Custom fields draft — separate rows in `project_design_brief_fields`,
  // held locally the same deferred-to-Save way as `draft.applicableCodes`.
  const [fields, setFields] = useState<DesignBriefField[]>([]);
  const [removedFieldIds, setRemovedFieldIds] = useState<string[]>([]);
  const [pendingRemoveIdx, setPendingRemoveIdx] = useState<number | null>(null);
  const [focusNewFieldIdx, setFocusNewFieldIdx] = useState<number | null>(null);
  const fieldTitleRefs = useRef<Record<number, HTMLInputElement | null>>({});

  useEffect(() => {
    if (open) {
      setDraft(ctx.designBrief.data || blankBrief(ctx.project.id));
      setFields(ctx.designBriefFields.data);
      setRemovedFieldIds([]);
      setDirty(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, ctx.designBrief.data, ctx.designBriefFields.data]);

  useEffect(() => {
    if (focusNewFieldIdx == null) return;
    fieldTitleRefs.current[focusNewFieldIdx]?.focus();
    setFocusNewFieldIdx(null);
  }, [focusNewFieldIdx]);

  // Radix's Dialog only restores focus to a <Dialog.Trigger> ref on close —
  // this kit's Sheets are always opened imperatively from a page-owned
  // button, never a <SheetTrigger>, so that ref is always null and focus
  // silently lands on <body> (DESIGN.md §7). Capture whatever was focused
  // right before open and restore it ourselves via onCloseAutoFocus.
  const openerRef = useRef<Element | null>(null);
  useEffect(() => {
    if (open) openerRef.current = document.activeElement;
  }, [open]);

  function update(patch: Partial<ProjectDesignBrief>) {
    setDraft((prev) => ({ ...prev, ...patch }));
    setDirty(true);
  }

  function updateCode(idx: number, patch: Partial<DesignBriefCode>) {
    const next = draft.applicableCodes.slice();
    next[idx] = { ...next[idx], ...patch };
    update({ applicableCodes: next });
  }

  function addCode() {
    update({ applicableCodes: [...draft.applicableCodes, { code: '', note: '' }] });
  }

  function removeCode(idx: number) {
    update({ applicableCodes: draft.applicableCodes.filter((_, i) => i !== idx) });
  }

  function addField() {
    setFields((prev) => {
      const next = [...prev, blankCustomField(ctx.project.id, prev.length)];
      setFocusNewFieldIdx(next.length - 1);
      return next;
    });
    setDirty(true);
  }

  function updateFieldTitle(idx: number, title: string) {
    setFields((prev) => prev.map((f, i) => (i === idx ? { ...f, title } : f)));
    setDirty(true);
  }

  function updateFieldValue(idx: number, value: string) {
    setFields((prev) => prev.map((f, i) => (i === idx ? { ...f, value } : f)));
    setDirty(true);
  }

  function moveField(idx: number, dir: -1 | 1) {
    const target = idx + dir;
    if (target < 0 || target >= fields.length) return;
    setFields((prev) => {
      const next = prev.slice();
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
    setDirty(true);
  }

  function confirmRemoveField() {
    if (pendingRemoveIdx == null) return;
    const field = fields[pendingRemoveIdx];
    setFields((prev) => prev.filter((_, i) => i !== pendingRemoveIdx));
    if (field.id) setRemovedFieldIds((prev) => [...prev, field.id]);
    setPendingRemoveIdx(null);
    setDirty(true);
  }

  function requestClose() {
    if (dirty) setConfirmDiscard(true);
    else onClose();
  }

  async function handleSave() {
    setSaving(true);
    try {
      await ctx.saveDesignBrief(draft);
      for (let i = 0; i < fields.length; i++) {
        await ctx.saveDesignBriefField({ ...fields[i], projectId: ctx.project.id, sortOrder: i });
      }
      for (const id of removedFieldIds) {
        await ctx.removeDesignBriefField(id);
      }
      setDirty(false);
      onClose();
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <Sheet open={open} onOpenChange={(o) => !o && requestClose()}>
        <SheetContent
          side="right"
          className="!w-[clamp(26rem,40vw,44rem)]"
          onCloseAutoFocus={(e) => {
            e.preventDefault();
            if (openerRef.current instanceof HTMLElement) openerRef.current.focus();
          }}
        >
          <SheetHeader>
            <SheetTitle>Design brief</SheetTitle>
          </SheetHeader>

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--space-4)',
              marginTop: 'var(--space-4)',
              flex: 1,
              overflowY: 'auto',
            }}
          >
            <div>
              <Label htmlFor="db-scope">Scope</Label>
              <textarea
                id="db-scope"
                value={draft.scope || ''}
                onChange={(e) => update({ scope: e.target.value })}
                rows={4}
                style={textareaStyle}
              />
            </div>

            <div>
              <Label>Applicable codes</Label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                {draft.applicableCodes.map((c, idx) => (
                  <div
                    key={idx}
                    style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}
                  >
                    <Input
                      placeholder="e.g. IBC 2021"
                      value={c.code}
                      onChange={(e) => updateCode(idx, { code: e.target.value })}
                      style={{ flex: '0 0 40%' }}
                    />
                    <Input
                      placeholder="Note (edition, amendment…)"
                      value={c.note || ''}
                      onChange={(e) => updateCode(idx, { note: e.target.value })}
                      style={{ flex: 1 }}
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={`Remove code ${c.code || idx + 1}`}
                      onClick={() => removeCode(idx)}
                    >
                      <Icon icon={X} size="sm" />
                    </Button>
                  </div>
                ))}
                <div>
                  <Button variant="outline" size="sm" onClick={addCode}>
                    <Icon icon={Plus} size="sm" /> Add code
                  </Button>
                </div>
              </div>
            </div>

            <div>
              <Label htmlFor="db-occupancy">Occupancy</Label>
              <Input
                id="db-occupancy"
                value={draft.occupancy || ''}
                onChange={(e) => update({ occupancy: e.target.value })}
              />
            </div>

            <div>
              <Label htmlFor="db-construction">Construction type</Label>
              <Input
                id="db-construction"
                value={draft.constructionType || ''}
                onChange={(e) => update({ constructionType: e.target.value })}
              />
            </div>

            <div>
              <Label htmlFor="db-owner">Owner preferences</Label>
              <textarea
                id="db-owner"
                value={draft.ownerPreferences || ''}
                onChange={(e) => update({ ownerPreferences: e.target.value })}
                rows={4}
                style={textareaStyle}
              />
            </div>

            <div>
              <Label htmlFor="db-criteria">Design criteria</Label>
              <textarea
                id="db-criteria"
                value={draft.designCriteria || ''}
                onChange={(e) => update({ designCriteria: e.target.value })}
                rows={4}
                style={textareaStyle}
              />
            </div>

            <div>
              <Label>Custom fields</Label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                {fields.map((f, idx) => (
                  <div
                    key={f.id || `new-${idx}`}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 'var(--space-1)',
                      padding: 'var(--space-2)',
                      border: '1px solid var(--bd)',
                      borderRadius: 'var(--radius-md)',
                      background: 'var(--sa)',
                    }}
                  >
                    <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
                      <Input
                        ref={(el) => {
                          fieldTitleRefs.current[idx] = el;
                        }}
                        aria-label={`Custom field ${idx + 1} title`}
                        placeholder="Field title"
                        value={f.title}
                        onChange={(e) => updateFieldTitle(idx, e.target.value)}
                        style={{ flex: 1, fontWeight: 600 }}
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={`Move ${f.title || 'field'} up`}
                        disabled={idx === 0}
                        onClick={() => moveField(idx, -1)}
                      >
                        <Icon icon={ArrowUp} size="sm" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={`Move ${f.title || 'field'} down`}
                        disabled={idx === fields.length - 1}
                        onClick={() => moveField(idx, 1)}
                      >
                        <Icon icon={ArrowDown} size="sm" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={`Remove ${f.title || 'field'}`}
                        onClick={() => setPendingRemoveIdx(idx)}
                      >
                        <Icon icon={X} size="sm" className="text-danger" />
                      </Button>
                    </div>
                    <textarea
                      aria-label={`Custom field ${idx + 1} value`}
                      placeholder="Value"
                      value={f.value || ''}
                      onChange={(e) => updateFieldValue(idx, e.target.value)}
                      rows={2}
                      style={textareaStyle}
                    />
                  </div>
                ))}
                <div>
                  <Button variant="outline" size="sm" onClick={addField}>
                    <Icon icon={Plus} size="sm" /> Add field
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <SheetFooter>
            <Button variant="outline" onClick={requestClose} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <ConfirmDialog
        open={confirmDiscard}
        onOpenChange={setConfirmDiscard}
        title="Discard changes?"
        description="You have unsaved edits to the design brief. Discarding closes the panel without saving."
        confirmLabel="Discard"
        destructive
        onConfirm={() => {
          setConfirmDiscard(false);
          setDirty(false);
          onClose();
        }}
      />

      <ConfirmDialog
        open={pendingRemoveIdx != null}
        onOpenChange={(o) => !o && setPendingRemoveIdx(null)}
        title="Remove field?"
        description={`"${
          pendingRemoveIdx != null ? fields[pendingRemoveIdx]?.title || 'This field' : ''
        }" is removed once you Save. Cancel here to keep it.`}
        confirmLabel="Remove"
        destructive
        onConfirm={confirmRemoveField}
      />
    </>
  );
}
